import React, { useRef, useEffect } from 'react';
import { TouchableOpacity, StyleSheet, View, Dimensions, Animated, Platform } from 'react-native';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import Icon from '../Icon';
import Button, { BUTTON_TYPES } from '../Button';
import * as Linking from 'expo-linking';
import { expenseReceiptEmail } from '../../config/env';
import BaseText from '../BaseText';
import PhotoUploads from './PhotoUploads';
import {
  EXPENSE_ACTION_TYPES,
  EXPENSE_RECEIPTS_SUB_TAB_OPTIONS,
  EXPENSE_SUB_TAB_OPTIONS,
} from '../../constants/Enums';
import { scaleVertically, scale } from '../../utils/scale.utils';
import ReportsToggle from './ReportsToggle';
import { isNullOrEmpty } from '../../utils/common.utils';
import { useDispatch, useSelector } from 'react-redux';
import { selectExpenseStates } from '../../store/selectors/expense.selector';
import { initializeExpenseState } from '../../utils/expense.utils';
import { ExpenseCategories } from '../../constants/ExpenseCategories';
import { initiateExpenseValues } from '../../store/actions/expense';
import { verticalBase } from '../../styles/CommonProperties';

const { height: screenHeight } = Dimensions.get('window');

const ExpenseCamera = ({
  activeTab,
  onTabChange,
  setEnableCamera,
  selectedImages,
  toggleSelectImage,
  resetSelectedImages,
}) => {
  const isIOS = Platform.OS === 'ios';
  const isIphone = isIOS && (Platform.isPad ? false : true);
  const isIpad = isIOS && Platform.isPad;
  const isChrome = Platform.OS === 'web' && /Chrome/.test(navigator.userAgent);
  const animation = useRef(new Animated.Value(0)).current;
  const dispatch = useDispatch();
  const actionType = EXPENSE_ACTION_TYPES.CREATION;
  const subTab = EXPENSE_SUB_TAB_OPTIONS.RECEIPTS;
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];

  useEffect(() => {
    Animated.timing(animation, {
      toValue: 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [activeTab]);

  useEffect(() => {
    // 1. Initialize form if needed
    if (isNullOrEmpty(expenseStates)) {
      const formFields = initializeExpenseState(ExpenseCategories);
      dispatch(initiateExpenseValues({ actionType, subTab, formFields }));
    }
  }, []);

  const handleCameraTabPress = () => {
    onTabChange(EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA);
    resetSelectedImages();
  };

  const handleUploadsTabPress = () => {
    if (
      activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL ||
      activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA
    ) {
      resetSelectedImages();
    }
    onTabChange(EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS);
  };

  const handleEmailTabPress = () => {
    onTabChange(EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL);
    resetSelectedImages();
  };

  const handleEmailPress = () => {
    Linking.openURL(`mailto:${expenseReceiptEmail}`);
  };

  return (
    <View>
      <View style={styles.buttonContainer}>
        <Animated.View style={{ opacity: animation }}>
          <Button
            label="Camera"
            labelStyle={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA
                ? styles.activeButtonLabelText2
                : styles.buttonLabelText2
            }
            style={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA
                ? styles.activeButtonStyle2
                : styles.buttonStyle
            }
            onPress={handleCameraTabPress}
            type={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA
                ? BUTTON_TYPES.OUTLINED
                : BUTTON_TYPES.TEXT
            }
          />
        </Animated.View>
        <Animated.View style={{ opacity: animation }}>
          <Button
            label="Uploads"
            labelStyle={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS
                ? styles.activeButtonLabelText2
                : styles.buttonLabelText2
            }
            style={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS
                ? styles.activeButtonStyle2
                : styles.buttonStyle
            }
            onPress={handleUploadsTabPress}
            type={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS
                ? BUTTON_TYPES.OUTLINED
                : BUTTON_TYPES.TEXT
            }
          />
        </Animated.View>
        <Animated.View style={{ opacity: animation }}>
          <Button
            label="Email"
            labelStyle={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL
                ? styles.activeButtonLabelText2
                : styles.buttonLabelText2
            }
            style={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL
                ? styles.activeButtonStyle2
                : styles.buttonStyle
            }
            onPress={handleEmailTabPress}
            type={
              activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL
                ? BUTTON_TYPES.OUTLINED
                : BUTTON_TYPES.TEXT
            }
          />
        </Animated.View>
      </View>
      <View style={CommonStyles.stepOneExpenseCard}>
        {activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA && (
          <TouchableOpacity style={styles.iconContainer} onPress={() => setEnableCamera(true)}>
            <Icon name="camera" size={Math.round(scaleVertically(89))} color={Color.primary} />
            <BaseText style={CommonProperties.custom}>Take receipt picture</BaseText>
          </TouchableOpacity>
        )}
        {activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS && (
          <PhotoUploads selectedImages={selectedImages} toggleSelectImage={toggleSelectImage} />
        )}
        {activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL && expenseReceiptEmail && (
          <View style={styles.forwardReceiptContainer}>
            {/* Top Section */}
            <View style={styles.topSection}>
              <BaseText style={styles.forwardText}>Forward email receipt to</BaseText>
              <TouchableOpacity onPress={handleEmailPress}>
                <BaseText
                  style={[
                    styles.emailText,
                    (isIphone || isIpad) && isChrome && {textDecorationLine: 'none'},
                  ]}
                >
                  {expenseReceiptEmail}
                </BaseText>
              </TouchableOpacity>
            </View>
            <View style={styles.mailIconContainer}>
              <Icon name="email" size={Math.round(scaleVertically(89))} color={Color.primary} />
            </View>
            {/* Bottom Section */}

            <BaseText style={styles.bottomText}>The image will appear in "Uploads"</BaseText>
          </View>
        )}
      </View>
      {activeTab !== EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.EMAIL && (
        <View style={styles.reportsMargin}>
          <ReportsToggle subTab={EXPENSE_SUB_TAB_OPTIONS.RECEIPTS} isDisabled={false} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    ...CommonProperties.row,
    ...CommonProperties.justifyBetween,
    ...CommonProperties.px3,
    ...CommonProperties.pt3,
    ...CommonProperties.pb5,
  },
  iconContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: scaleVertically(18),
  },
  mailIconContainer: {
    ...CommonProperties.centerItems,
  },
  forwardReceiptContainer: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 24,
    alignItems: 'center',
  },
  topSection: {},
  forwardText: {
    ...CommonStyles.custom,
    color: Color.black,
    textAlign: 'center',
  },
  emailText: {
    ...CommonStyles.custom,
    color: Color.primary,
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  bottomText: {
    ...CommonStyles.custom,
    color: Color.black,
    textAlign: 'center',
  },
  buttonLabelText2: {
    ...CommonProperties.custom,
    color: Color.black,
  },
  activeButtonLabelText2: {
    ...CommonProperties.custom,
    color: Color.active,
  },
  buttonStyle: {
    ...CommonProperties.borderRadius4,
    width: scale(88),
    backgroundColor: Color.transparent,
    ...CommonStyles.p2,
  },
  activeButtonStyle2: {
    ...CommonProperties.borderRadius4,
    width: scale(88),
    borderWidth: 2,
    borderColor: Color.active,
    ...CommonStyles.p2,
  },
  reportsMargin: {
    marginTop: verticalBase,
  },
});

export default ExpenseCamera;
